import { useEffect, useRef, useState } from "react";
import {
  dimMigrations,
  dimProps,
  type DimRef,
  type DimShape,
} from "sketch/shapes/dim";
import { formatPageUnits, measurementToPageUnits } from "sketch/units";
import {
  Edge2d,
  Group2d,
  Rectangle2d,
  SVGContainer,
  ShapeUtil,
  Vec,
  type Editor,
  type Geometry2d,
  type TLIndicatorPath,
  type TLResizeInfo,
} from "tldraw";

import { annotationScale } from "@/sketch/annotation-scale";
import {
  measureSheetText,
  SHEET_ASCENT,
  SHEET_DESCENT,
  SHEET_FONT,
  SHEET_FONT_EXPORT,
  SHEET_FONT_FACE,
  SHEET_MASK_PAD,
  SHEET_SIZE,
  SHEET_TRACKING,
  sheetCase,
} from "@/sketch/lettering";

import { driveDim } from "./dim-drive";
import {
  chainSiblings,
  dimLayout,
  dimOffset,
  offsetGrowthUnit,
  type DimLayout,
} from "./dim-geometry";

/**
 * A linear dimension in Bear's vocabulary. Endpoints live in props as
 * absolute page points (the shape's own x/y stay 0); bindings override them
 * and DimBindingUtil writes resolved points back so geometry and sync stay
 * fresh. Dragging the dim line adjusts its offset, never the measured points.
 */
const TICK = 9;
const LABEL_W = 110;
const LABEL_H = 22;
/** Extension lines stand off the geometry and overshoot the dim line. */
const EXT_GAP = 6;
const EXT_OVERSHOOT = 4;

interface DimColors {
  stroke: string;
  /** What the value's background mask is filled with. */
  mask: string;
  font: string;
}

const CANVAS_COLORS: DimColors = {
  stroke: "var(--tl-color-text-1)",
  mask: "var(--tl-color-background)",
  font: SHEET_FONT,
};

/** Exports have no tldraw CSS variables, so they name colors and the
 *  embedded font family outright. */
const EXPORT_COLORS: DimColors = {
  stroke: "#1d1d1d",
  mask: "#fff",
  font: SHEET_FONT_EXPORT,
};

/** The full dim rendering, shared by the canvas component and SVG export.
 *  The label always reads the measured reality (values two-way sync), drawn
 *  parallel to the dim line like a drafter letters it. Annotation graphics
 *  are screen-constant: `k` (1/zoom) scales ticks, text, and line weights so
 *  a dim reads the same on a 1 1/2" detail as on a floorplan. The terminator
 *  states the reference: tick = face of stud, arrow = face of finish,
 *  dot = a construction line the drafter draws (typically a centerline). */
function dimSvg(
  editor: Editor,
  layout: DimLayout,
  colors: DimColors,
  k: number,
  refs: [DimRef, DimRef],
) {
  const { a, b, lineA, lineB, mid, measured } = layout;
  const label = sheetCase(formatPageUnits(measured) || `0"`);
  const labelW = measureSheetText(editor, label) * k;
  const color = colors.stroke;

  // Parallel to the dim line, never upside down. Vertical reads bottom-up
  // (drafting convention), which puts the text left of the line.
  let angle =
    (Math.atan2(lineB.y - lineA.y, lineB.x - lineA.x) * 180) / Math.PI;
  if (angle >= 90 || angle < -90) angle += 180;

  const dir = Vec.Sub(lineB, lineA).uni();
  // 45-degree architectural ticks at the dim line ends.
  const tickFor = (p: Vec) => {
    const t = new Vec(dir.x - dir.y, dir.y + dir.x).mul((TICK * k) / 2);
    return { x1: p.x - t.x, y1: p.y - t.y, x2: p.x + t.x, y2: p.y + t.y };
  };
  // Arrowheads point outward along the dim line at each end.
  const arrowFor = (tip: Vec, out: Vec) => {
    const back = Vec.Sub(tip, out.clone().mul(8 * k));
    const side = new Vec(-out.y, out.x).mul(2.5 * k);
    return `M ${tip.x} ${tip.y} L ${back.x + side.x} ${back.y + side.y} L ${back.x - side.x} ${back.y - side.y} Z`;
  };
  const terminator = (p: Vec, i: number) => {
    const ref = refs[i]!;
    if (ref === "construction") {
      return (
        <circle key={`t${i}`} cx={p.x} cy={p.y} r={2.5 * k} fill={color} />
      );
    }
    if (ref === "finish") {
      const out = i === 0 ? dir.clone().neg() : dir.clone();
      return <path key={`t${i}`} d={arrowFor(p, out)} fill={color} />;
    }
    const t = tickFor(p);
    return <line key={`t${i}`} {...t} stroke={color} strokeWidth={1.25 * k} />;
  };

  return (
    <g>
      {[
        { from: a, to: lineA },
        { from: b, to: lineB },
      ].map(({ from, to }, i) => {
        const span = Vec.Sub(to, from);
        const len = span.len();
        if (len === 0) return null;
        const u = span.uni();
        const gap = Math.min(EXT_GAP * k, len / 2);
        const start = Vec.Add(from, u.clone().mul(gap));
        const end = Vec.Add(to, u.clone().mul(EXT_OVERSHOOT * k));
        return (
          <line
            key={i}
            x1={start.x}
            y1={start.y}
            x2={end.x}
            y2={end.y}
            stroke={color}
            strokeWidth={0.75 * k}
          />
        );
      })}
      <line
        x1={lineA.x}
        y1={lineA.y}
        x2={lineB.x}
        y2={lineB.y}
        stroke={color}
        strokeWidth={1 * k}
      />
      {[lineA, lineB].map((p, i) => terminator(p, i))}
      <g transform={`rotate(${angle}, ${mid.x}, ${mid.y})`}>
        {/* The same background mask a note gets: the grid must not read
            through the value. */}
        <rect
          x={mid.x - labelW / 2 - SHEET_MASK_PAD * k}
          y={mid.y - (8 + SHEET_ASCENT * SHEET_SIZE + SHEET_MASK_PAD) * k}
          width={labelW + SHEET_MASK_PAD * 2 * k}
          height={
            ((SHEET_ASCENT + SHEET_DESCENT) * SHEET_SIZE + SHEET_MASK_PAD * 2) *
            k
          }
          fill={colors.mask}
        />
        <text
          x={mid.x}
          y={mid.y - 8 * k}
          textAnchor="middle"
          fontSize={SHEET_SIZE * k}
          fontFamily={colors.font}
          letterSpacing={SHEET_TRACKING * SHEET_SIZE * k}
          fill={color}
        >
          {label}
        </text>
      </g>
    </g>
  );
}

export class DimShapeUtil extends ShapeUtil<DimShape> {
  static override type = "dim" as const;
  static override props = dimProps;
  // The app store derives its schema from the utils, so the migrations
  // must ride here too, or the two sides disagree about the dim sequence.
  static override migrations = dimMigrations;

  override getDefaultProps(): DimShape["props"] {
    return {
      value: null,
      direction: "x",
      offset: null,
      chain: null,
      refStart: "stud",
      refEnd: "stud",
      start: { x: 0, y: 0 },
      end: { x: 96, y: 0 },
    };
  }

  override canResize() {
    return false;
  }

  /** Annotation, not geometry: never a bounds-snap target (its bounds
   *  include the label box and ticks, which would snap shapes off-grid). */
  override getBoundsSnapGeometry() {
    return { points: [] };
  }

  /** Same as the callout: the indicator is the dim line, and a box around
   *  the extension lines and label is noise. */
  override hideSelectionBoundsFg() {
    return true;
  }

  override hideResizeHandles() {
    return true;
  }

  override hideRotateHandle() {
    return true;
  }

  /** Double-click the label to type a value; committing drives the geometry. */
  override canEdit() {
    return true;
  }

  /** What this shape says: the value on the sheet, authored or measured. */
  override getText(shape: DimShape) {
    return formatPageUnits(dimLayout(this.editor, shape).measured);
  }

  /** The sheet face, so tldraw loads it (which re-measures the value once it
   *  lands) and embeds it in exports. */
  override getFontFaces() {
    return [SHEET_FONT_FACE];
  }

  override onResize(shape: DimShape, _info: TLResizeInfo<DimShape>) {
    return shape;
  }

  /** Screen-constant annotation scale. Reading the zoom signal here keeps
   *  geometry honest too: the computed cache tracks it and re-derives. */
  private annotationScale(): number {
    return annotationScale(this.editor);
  }

  override getGeometry(shape: DimShape): Geometry2d {
    const { lineA, lineB, mid } = dimLayout(this.editor, shape);
    const k = this.annotationScale();
    return new Group2d({
      children: [
        new Edge2d({ start: lineA, end: lineB }),
        new Rectangle2d({
          x: mid.x - (LABEL_W * k) / 2,
          y: mid.y - (LABEL_H + 2) * k,
          width: LABEL_W * k,
          height: LABEL_H * k,
          isFilled: true,
        }),
      ],
    });
  }

  override component(shape: DimShape) {
    const layout = dimLayout(this.editor, shape);
    const k = this.annotationScale();
    const isEditing = this.editor.getEditingShapeId() === shape.id;
    return (
      <SVGContainer>
        {dimSvg(this.editor, layout, CANVAS_COLORS, k, [
          shape.props.refStart,
          shape.props.refEnd,
        ])}
        {isEditing && (
          <foreignObject
            x={layout.mid.x - 70 * k}
            y={layout.mid.y - 32 * k}
            width={140 * k}
            height={28 * k}
          >
            <DimValueInput
              editor={this.editor}
              shape={shape}
              measured={layout.measured}
              scale={k}
            />
          </foreignObject>
        )}
      </SVGContainer>
    );
  }

  override toSvg(shape: DimShape) {
    // Exports bake the current zoom's annotation scale, so a screenshot of a
    // detail reads like the canvas the user (or agent) was looking at.
    return dimSvg(
      this.editor,
      dimLayout(this.editor, shape),
      EXPORT_COLORS,
      this.annotationScale(),
      [shape.props.refStart, shape.props.refEnd],
    );
  }

  override getIndicatorPath(shape: DimShape): TLIndicatorPath {
    const { lineA, lineB } = dimLayout(this.editor, shape);
    const path = new Path2D();
    path.moveTo(lineA.x, lineA.y);
    path.lineTo(lineB.x, lineB.y);
    return path;
  }

  /** Dragging a dim adjusts its offset (bound) or moves it whole (free).
   *  The drag is what authors an offset: it takes the dim (and its chain)
   *  out of the automatic stack and puts it where the drafter wants it. */
  override onTranslateEnd(initial: DimShape, current: DimShape) {
    const delta = new Vec(current.x - initial.x, current.y - initial.y);
    if (delta.len() === 0) return;
    const bindings = this.editor.getBindingsFromShape(current.id, "dim");
    const { start, end } = initial.props;

    if (bindings.length === 0) {
      this.editor.updateShape<DimShape>({
        id: current.id,
        type: "dim",
        x: 0,
        y: 0,
        props: {
          start: { x: start.x + delta.x, y: start.y + delta.y },
          end: { x: end.x + delta.x, y: end.y + delta.y },
        },
      });
      return;
    }

    // Project the drag onto the same normal dimLayout places the line along,
    // so the line lands where the gesture left it for every direction. The
    // line the gesture authors belongs to the string, so it lives on the dim
    // that was dragged and its siblings go back to following it: two drags
    // in one chain must not leave the chain on two lines.
    const grow = delta.dpr(offsetGrowthUnit(this.editor, initial));
    this.editor.updateShapes<DimShape>([
      {
        id: current.id,
        type: "dim",
        x: 0,
        y: 0,
        props: { offset: dimOffset(this.editor, initial) + grow },
      },
      ...chainSiblings(this.editor, initial).map((sibling) => ({
        id: sibling.id,
        type: "dim" as const,
        props: { offset: null },
      })),
    ]);
  }
}

function safeParse(value: string): number | null {
  try {
    return measurementToPageUnits(value);
  } catch {
    return null;
  }
}

function DimValueInput({
  editor,
  shape,
  measured,
  scale,
}: {
  editor: Editor;
  shape: DimShape;
  measured: number;
  scale: number;
}) {
  const [text, setText] = useState(formatPageUnits(measured) || `0"`);
  const textRef = useRef(text);
  textRef.current = text;
  const initial = useRef(text);
  const cancelled = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // `autoFocus` fires before tldraw refocuses its container on edit-start,
  // so the input claims focus on the next frame (and once more after the
  // container settles), the same lesson the first sketch prototype learned.
  useEffect(() => {
    const focus = () => {
      const el = inputRef.current;
      if (el && document.activeElement !== el) {
        el.focus();
        el.select();
      }
    };
    const raf = requestAnimationFrame(focus);
    const timer = setTimeout(focus, 80);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, []);

  // tldraw's editing keymap swallows Enter/Escape in the capture phase and
  // exits editing (unmounting this input), so the commit rides the unmount:
  // Enter, clicking away, and tool switches all commit; Escape cancels.
  useEffect(() => {
    return () => {
      if (cancelled.current) return;
      const value = textRef.current.trim();
      // Untouched text is not intent: clicking onward in a chain run (which
      // unmounts this input) must not stamp a value the user never typed.
      if (value === initial.current.trim()) return;
      const parsed = safeParse(value);
      if (parsed == null) return; // not a measurement; drop the edit
      editor.updateShape<DimShape>({
        id: shape.id,
        type: "dim",
        props: { value },
      });
      const fresh = editor.getShape<DimShape>(shape.id);
      if (fresh) driveDim(editor, fresh, parsed);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <input
      ref={inputRef}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") editor.setEditingShape(null); // unmount commits
        if (e.key === "Escape") {
          cancelled.current = true;
          editor.setEditingShape(null);
        }
      }}
      style={{
        width: "100%",
        height: "100%",
        textAlign: "center",
        // CSS px inside a foreignObject are page units; scale keeps the
        // input's text and chrome screen-constant like the rest of the dim.
        fontSize: SHEET_SIZE * scale,
        fontFamily: SHEET_FONT,
        border: `${scale}px solid var(--tl-color-selected)`,
        borderRadius: 3 * scale,
        background: "var(--tl-color-background)",
        color: "var(--tl-color-text-1)",
        outline: "none",
      }}
    />
  );
}
